// Presentation only. Native Shopify contextual prices and cart guards stay authoritative.
const id = value => typeof value === 'string' && /^[1-9]\d*$/.test(value);
const cents = value => Number.isSafeInteger(value) && value > 0 && value <= 100000000;
export function retailComparison(feed, expected) {
  if (!id(expected.productId) || !id(expected.variantId) || !expected.sku || !cents(expected.priceCents) || expected.currency !== 'USD') return null;
  if (feed?.source !== 'shopify-online-store-liquid' || feed.currency !== expected.currency || feed.store !== 'j11sgn-gq.myshopify.com' || feed.context?.kind !== 'product' || feed.context.resource !== expected.handle || feed.products?.length !== 1) return null;
  const product = feed.products[0];
  if (product.productId !== expected.productId || product.handle !== expected.handle || !Array.isArray(product.variants)) return null;
  const variants = product.variants.filter(row => row.variantId === expected.variantId || row.sku === expected.sku);
  if (variants.length !== 1 || variants[0].variantId !== expected.variantId || variants[0].sku !== expected.sku || !cents(variants[0].priceCents) || variants[0].priceCents <= expected.priceCents) return null;
  const retailCents = variants[0].priceCents, savedCents = retailCents - expected.priceCents;
  return {retailCents, priceCents: expected.priceCents, savedCents, percent: Math.round(savedCents / retailCents * 100)};
}
export function installB2BOriginalPrices({doc = document, config = window.FIF_PREVIEW_CONFIG, fetcher = fetch} = {}) {
  if (config?.b2bPricing !== true) return;
  const pending = new WeakSet(), cache = new Map(), money = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});
  const read = async expected => {
    const key = `${expected.handle}|${expected.currency}`;
    if (!cache.has(key)) cache.set(key, (async () => {
      const response = await fetcher(`/products/${encodeURIComponent(expected.handle)}?section_id=fif-native-product-feed&internal=1`, {credentials: 'omit', cache: 'no-store', headers: {accept: 'text/html'}, signal: AbortSignal.timeout(10000)});
      if (!response.ok) throw Error('Retail lookup unavailable');
      const body = await response.text(); if (body.length > 2000000) throw Error('Retail response too large');
      const parsed = new DOMParser().parseFromString(body, 'text/html'), scripts = parsed.querySelectorAll('script[data-fif-native-catalog-page]');
      if (scripts.length !== 1) throw Error('Retail projection missing');
      return JSON.parse(scripts[0].textContent);
    })());
    return cache.get(key);
  };
  async function decorate(node) {
    if (pending.has(node) || node.dataset.fifB2bCompared === 'true') return;
    pending.add(node);
    const snapshot = {...node.dataset};
    const expected = {productId: snapshot.productId, variantId: snapshot.variantId, sku: snapshot.sku, handle: snapshot.handle, currency: snapshot.currency, priceCents: Number(snapshot.priceCents)};
    try {
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(expected.handle || '')) return;
      const result = retailComparison(await read(expected), expected);
      if (!result || !node.isConnected || JSON.stringify({...node.dataset}) !== JSON.stringify(snapshot)) return;
      const box = doc.createElement('span'); box.className = 'fif-b2b-comparison';
      const original = doc.createElement('s'); original.textContent = money.format(result.retailCents / 100); original.setAttribute('aria-label', `Regular price ${original.textContent}`);
      const savings = doc.createElement('small'); savings.textContent = `You save ${money.format(result.savedCents / 100)} (${result.percent}%)`;
      box.append(original, savings); node.prepend(box); node.dataset.fifB2bCompared = 'true';
    } catch { /* Unknown retail values never alter the actual shop price. */ }
    finally { pending.delete(node); }
  }
  const scan = () => { for (const node of doc.querySelectorAll('[data-fif-b2b-price]')) void decorate(node); };
  scan();
  const observer = new MutationObserver(records => { if (records.some(record => record.addedNodes.length)) scan(); });
  observer.observe(doc.body, {childList: true, subtree: true});
  return () => observer.disconnect();
}

if(typeof document!=="undefined")installB2BOriginalPrices();
